import { Divider, Stack, Typography, useTheme } from "@mui/material";
import { FC } from "react";
import { ConfirmationDialog } from "../../../components";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLocalization } from "../../../hooks/useLocalization";
import { useAddAndVerifyEmails } from "../hooks/useAddAndVerifyEmails";
import { useEmailExtractor } from "../hooks/useEmailExtractor";
import { AddedEmailsList } from "./AddedEmailsList";
import { CsvImportButton } from "./CsvImportButton";
import { WhitelistEmailForm } from "./WhitelistEmailForm";

export interface AddToWhitelistDialogProps {
  isOpen: boolean;
  onClose: () => void;
  whiteListedEmails?: string[];
  licenseId: string;
}

export const AddToWhitelistDialog: FC<AddToWhitelistDialogProps> = ({
  isOpen,
  onClose,
  whiteListedEmails = [],
  licenseId,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const handleCloseAndResetEmailExtractor = () => {
    reset();
    onClose();
  };

  const {
    form,
    emailValue,
    emailError,
    addedEmailAddresses,
    handleFormSubmit,
    handleCancel,
    handleRemoveEmail,
    handleSubmit,
    addMultipleEmails,
    isSubmitDisabled,
    isMutationPending,
    mutationError,
  } = useAddAndVerifyEmails({
    whiteListedEmails,
    onClose: handleCloseAndResetEmailExtractor,
    licenseId,
  });

  const onEmailsExtracted = (emails: string[]) => {
    addMultipleEmails(emails);
  };

  const { handleFileSelect, reset, parsedData, fileName } = useEmailExtractor(
    whiteListedEmails,
    onEmailsExtracted,
  );

  const onFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    void handleFileSelect(event);
  };

  const isMobile = useIsMobile();
  const actionText = isMobile
    ? t("licenses.addToWhitelistDialog.actionTextShort")
    : t("licenses.addToWhitelistDialog.actionTextLong");

  return (
    <ConfirmationDialog.Root
      fullScreenOnMobile
      fullWidth
      maxWidth="xs"
      onClose={handleCancel}
      open={isOpen}
    >
      <ConfirmationDialog.Title showIcon={false}>
        <Stack>
          {t("licenses.addToWhitelistDialog.title")}
          <Typography fontSize={12} sx={{ color: theme.colors.text[1] }}>
            {t("licenses.addToWhitelistDialog.description")}
          </Typography>
        </Stack>
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content error={mutationError}>
        <Stack sx={{ gap: 3 }}>
          <WhitelistEmailForm
            emailError={emailError}
            emailValue={emailValue}
            form={form}
            onSubmit={handleFormSubmit}
          />
          <Divider>
            <Typography color="text.secondary">
              {t("licenses.addToWhitelistDialog.or")}
            </Typography>
          </Divider>
          <CsvImportButton onFileSelect={onFileSelect} />
          <AddedEmailsList
            addedEmailAddresses={addedEmailAddresses}
            alreadyWhitelistedCount={parsedData?.alreadyWhitelisted.length}
            duplicatesCount={parsedData?.duplicatesInFile.length}
            fileName={fileName}
            invalidEmailsCount={parsedData?.invalidEmails.length}
            onRemoveEmail={handleRemoveEmail}
          />
        </Stack>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleCancel}
          sx={{ color: theme.colors.primary[5] }}
          variant="secondary"
        >
          {t("licenses.addToWhitelistDialog.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={isSubmitDisabled}
          isLoading={isMutationPending}
          loadingText={t("licenses.addToWhitelistDialog.adding")}
          onClick={handleSubmit}
          variant="contained"
        >
          {actionText}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
